// src/pages/ReferralCodePage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, KeyRound, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { verifyReferralCode, markReferralCodeAsUsed, createAssessment } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { useFormContext } from '../context/FormContext';
import { logoutUser } from '../firebase';

const ReferralCodePage = () => {
  const navigate = useNavigate();
  const { currentUser, hasValidReferralCode, setReferralVerified, resetAuthState } = useAuth();
  const { setAssessmentId } = useFormContext();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [redirecting, setRedirecting] = useState(false);
  
  // Redirect to questions if already verified
  useEffect(() => {
    if (hasValidReferralCode && currentUser) {
      navigate('/questions');
    }
  }, [hasValidReferralCode, currentUser, navigate]);
  
  // Redirect to login if not logged in
  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
    }
  }, [currentUser, navigate]);
  
  const handleCodeChange = (e) => {
    setCode(e.target.value.toUpperCase());
    if (error) setError('');
    if (success) setSuccess('');
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!code.trim()) {
      setError('请输入邀请码');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      // Verify the code
      const result = await verifyReferralCode(code.trim());
      
      if (!result.valid) {
        setError(result.message);
        return;
      }
      
      // Mark code as used
      await markReferralCodeAsUsed(result.codeId, currentUser.uid);
      
      // Create a new assessment
      const assessmentId = await createAssessment(currentUser.uid, result.codeId);
      
      // Store assessment ID in context and localStorage
      setAssessmentId(assessmentId);
      localStorage.setItem('assessmentId', assessmentId);
      
      // Show success message
      setSuccess('邀请码验证成功！正在跳转...');
      
      // Set as verified in context
      setReferralVerified(true);
      
      // Redirect after a short delay
      setRedirecting(true);
      setTimeout(() => {
        navigate('/questions');
      }, 1500);
      
    } catch (error) {
      console.error("Error verifying code:", error);
      setError('验证邀请码时发生错误，请稍后再试');
    } finally {
      setLoading(false);
    }
  };
  
  const handleLogout = async () => {
    try {
      await logoutUser();
      resetAuthState();
      navigate('/login');
    } catch (error) {
      console.error("Logout error:", error);
    }
  };
  
  const goToWelcome = () => {
    navigate('/');
  };
  
  return (
    <>
      {/* Fixed header with logo */}
      <header className="fixed top-0 left-0 right-0 bg-white shadow-sm py-3 z-10">
        <div className="container mx-auto px-4">
          <div className="flex justify-center">
            <img src="/new-logo-blue.png" alt="Careers Compass" className="h-10 object-contain" />
          </div>
        </div>
      </header>

      {/* Main content - positioned below fixed header */}
      <div className="w-full max-w-md mx-auto mt-20">
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-8 w-full">
          <h2 className="text-2xl font-bold text-center text-blue-600 mb-2">输入邀请码</h2>
          <div className="h-1 w-24 bg-gradient-to-r from-blue-400 to-blue-600 mx-auto mb-6 rounded-full"></div>
          
          <p className="text-gray-600 text-center mb-8">
            请输入您收到的邀请码以开始职业评估。如果您没有邀请码，请联系我们的顾问。
          </p>
          
          <form onSubmit={handleSubmit} className="space-y-4 mb-6">
            {/* Error message */}
            {error && (
              <div className="bg-red-50 text-red-700 p-3 rounded-lg mb-6 text-sm border border-red-100 flex items-start">
                <AlertCircle size={18} className="mr-2 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
            
            {/* Success message */}
            {success && (
              <div className="bg-green-50 text-green-700 p-3 rounded-lg mb-6 text-sm border border-green-100 flex items-start">
                <CheckCircle size={18} className="mr-2 flex-shrink-0 mt-0.5" />
                <span>{success}</span>
              </div>
            )}
            
            {/* Referral code input */}
            <div className="space-y-2">
              <label className="block text-gray-700 font-medium mb-1">邀请码</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <KeyRound size={18} className="text-gray-400" />
                </div>
                <input 
                  type="text" 
                  value={code}
                  onChange={handleCodeChange}
                  disabled={loading || redirecting}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none disabled:bg-gray-100 disabled:opacity-70"
                  placeholder="请输入您的邀请码"
                  maxLength={12}
                />
              </div>
            </div>
            
            {/* Submit button */}
            <button 
              type="submit"
              disabled={loading || redirecting || !code.trim()}
              className="w-full flex items-center justify-center py-3 px-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-colors shadow-md disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader size={16} className="animate-spin mr-2" />
                  <span className="font-medium">验证中...</span>
                </>
              ) : redirecting ? (
                <>
                  <CheckCircle size={16} className="mr-2" />
                  <span className="font-medium">验证成功，跳转中...</span>
                </>
              ) : (
                <span className="font-medium">验证</span>
              )}
            </button>
          </form>
          
          <div className="mt-6 text-center text-sm text-gray-500">
            <p>没有邀请码？联系Careers Compass小助手（微信号）：<p className="text-blue-600 hover:underline">yh1121022998_</p></p>
          </div>
          
          <div className="mt-6 text-center border-t border-gray-100 pt-6 flex justify-between">
            <button 
              onClick={goToWelcome}
              className="text-blue-600 hover:text-blue-700 text-sm flex items-center"
            >
              <ArrowLeft size={16} className="mr-1" />
              返回首页
            </button>
            
            <button 
              onClick={handleLogout}
              className="text-gray-500 hover:text-gray-700 text-sm"
            >
              切换账号
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReferralCodePage;